/**
 * 인증/권한 확인 훅
 *
 * Redux 스토어의 사용자 정보를 기반으로 권한(permission) 및 역할(role)을 확인한다.
 * - hasPermission: 'module:entity:action' 패턴의 권한 문자열을 AUTH 객체에서 검사
 * - hasRole: 역할 레벨 기반 비교 (SUPER_ADMIN > ADMIN > DATA_MANAGER > REVIEWER > VIEWER)
 * - isAdmin: ADMIN 이상 역할 여부
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// ─── 역할 레벨 정의 ─────────────────────────────────────────
const ROLE_LEVELS = {
  SUPER_ADMIN: 5,
  ADMIN: 4,
  DATA_MANAGER: 3,
  REVIEWER: 2,
  VIEWER: 1,
};

/**
 * 인증/권한 확인 훅
 * @returns {{ hasPermission: Function, hasRole: Function, isAdmin: boolean, user: Object }}
 */
const useAuth = () => {
  const user = useSelector((state) => state.auth.user);
  const authInitialized = useSelector((state) => state.auth.initialized);
  const authLoading = useSelector((state) => state.auth.loading);

  /**
   * 권한 확인
   * AUTH 객체에서 'module:entity:action' 패턴을 검사한다.
   *
   * 매칭 규칙 (백엔드 permissions.hasPermission과 동일):
   *   1. 정확히 일치하는 권한 (auth['std:application:write'])
   *   2. 엔티티 wildcard — 'std:*:write'
   *   3. 모듈 wildcard — '*:application:write'
   *   4. 전체 wildcard — '*' (SUPER_ADMIN)
   *   5. 액션 wildcard — 'std:application:*'
   *   6. 'manage' 액션은 모든 액션에 대응 — 'std:application:manage' → write/read/delete 전부 허용
   *
   * @param {string} permission - 'module:entity:action' 형식의 권한 문자열
   * @returns {boolean} 권한 보유 여부
   */
  const hasPermission = useMemo(() => {
    return (permission) => {
      if (!user?.AUTH) return false;

      const auth = user.AUTH;

      // SUPER_ADMIN 전체 권한
      if (auth['*']) return true;

      // 정확히 일치
      if (auth[permission]) return true;

      // wildcard 매칭 — 백엔드 permissions.js의 hasPermission 로직과 동일
      const [reqModule, reqEntity, reqAction] = permission.split(':');
      if (!reqModule || !reqEntity || !reqAction) return false;

      return Object.keys(auth).some((permKey) => {
        const [permModule, permEntity, permAction] = permKey.split(':');
        if (!permModule || !permEntity || !permAction) return false;
        return (
          (permModule === reqModule || permModule === '*') &&
          (permEntity === reqEntity || permEntity === '*') &&
          (permAction === reqAction || permAction === '*' || permAction === 'manage')
        );
      });
    };
  }, [user]);

  /**
   * 역할 레벨 비교
   * 현재 사용자의 역할이 지정된 역할 이상인지 확인한다.
   *
   * @param {string} role - 비교 대상 역할명
   * @returns {boolean} 지정 역할 이상 여부
   */
  const hasRole = useMemo(() => {
    return (role) => {
      if (!user?.role) return false;

      const userLevel = ROLE_LEVELS[user.role] || 0;
      const requiredLevel = ROLE_LEVELS[role] || 0;

      return userLevel >= requiredLevel;
    };
  }, [user]);

  /** ADMIN 이상 역할 여부 */
  const isAdmin = useMemo(() => {
    if (!user?.role) return false;
    const userLevel = ROLE_LEVELS[user.role] || 0;
    return userLevel >= ROLE_LEVELS.ADMIN;
  }, [user]);

  return {
    user,
    hasPermission,
    hasRole,
    isAdmin,
    authInitialized,
    authLoading,
  };
};

export default useAuth;
